import React, {useEffect, useMemo, useState} from 'react';
import {SelectProps} from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Pagination from '@mui/material/Pagination';
import {useGetFeeProductMutation, useSearchVariantsMutation} from 'services/classes';
import Button from "../Button";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

interface ISelect {
    id: string;
    label: string;
    onChange: (value: any) => void;
}

export type TSelectProps = SelectProps & ISelect;

const ITEMS_PER_PAGE = 4;

const ShopifySearch: React.FC<any> = ({
                                                   id = 'default-select',
                                                   label = 'default single select',
                                                   onChange,
                                                   value,
                                                   initialValue,
                                                   isFeeProduct = false,
                                                   ...restProps
                                               }) => {
    const [isResultsOpen, setIsResultsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedValue, setSelectedValue] = useState<any>(null);

    const [searchVariants, { data: searchResults, isLoading: isSearchLoading }] = useSearchVariantsMutation();
    const [getFeeProduct, { data: feeProduct, isLoading: isFeeProductLoading }] = useGetFeeProductMutation();

    useEffect(() => {
        if (isFeeProduct && feeProduct && feeProduct.variants.length > 0) {
            const feeVariant = feeProduct.variants[0]; // Assume the first variant is the one we want
            setSelectedValue(feeVariant);
            onChange(feeVariant);
        }
    }, [isFeeProduct, feeProduct, onChange]);

    useEffect(() => {
        if (isFeeProduct) {
            getFeeProduct();
        }
    }, [isFeeProduct, getFeeProduct])

    useEffect(() => {
        if (isFeeProduct && feeProduct && feeProduct.variants.length > 0) {
            const feeVariant = feeProduct.variants[0]; // Assume the first variant is the one we want
            setSelectedValue(feeVariant);
            onChange(feeVariant);
        }
    }, [isFeeProduct, feeProduct, onChange]);

    const handleSearch = () => {
        searchVariants({ query: searchTerm });
        setIsResultsOpen(true);
        setCurrentPage(1);
    };

    const paginatedResults = useMemo(() => {
        if (!searchResults?.variants) return [];
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return searchResults.variants.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [searchResults, currentPage]);

    const totalPages = useMemo(() => {
        return searchResults?.variants ? Math.ceil(searchResults.variants.length / ITEMS_PER_PAGE) : 0;
    }, [searchResults]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handleSelect = (value: any) => {
        setSelectedValue(value);
        onChange(value);
        setIsResultsOpen(false);
    };

    const handleRemove = () => {
        setSelectedValue(null);
        onChange(null);
    };

    const renderVariantItem = (variant: any, isSelected: boolean = false) => (
        <div className="flex items-center p-2 hover:bg-gray-100 cursor-pointer justify-between">
            <div className="flex items-center">
                {variant.product?.featuredImage && (
                    <img
                        src={variant.product.featuredImage.url}
                        alt={variant.product.title}
                        className="w-10 h-10 object-cover mr-3"
                    />
                )}
                <div>
                    <div className="font-semibold">{variant.product?.title || variant.title}</div>
                    <div className="text-sm text-gray-600">{variant.title} - ${variant.price}</div>
                </div>
            </div>
            {isSelected && (
                <DeleteOutlineOutlinedIcon
                    className="cursor-pointer ml-auto"
                    onClick={(e) => { e.stopPropagation(); handleRemove(); }}
                    color="error"
                />
            )}
        </div>
    );


    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    if (isFeeProduct) {
        return (
            <div>
                {isFeeProductLoading ? (
                    <div>Loading fee product...</div>
                ) : selectedValue ? (
                    <div className="border p-2 rounded mb-2">
                        {renderVariantItem(selectedValue, true)}
                    </div>
                ) : (
                    <div>No fee product found</div>
                )}
            </div>
        );
    }
    return (
        <div className="relative">
            <div>
                <div className="flex justify-between mb-4 gap-2">
                    <TextField
                        value={searchTerm}
                        onKeyDown={handleKeyDown} // Add this line to listen for keyboard events
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search for product from Shopify"
                        className="flex-grow mr-2"
                    />
                    <Button onClick={handleSearch}>Search</Button>
                </div>
                {selectedValue && (
                    <div className="border p-2 rounded mb-2">
                        {renderVariantItem(selectedValue, true)}
                    </div>
                )}
            </div>

            {isResultsOpen && (
                <div className="absolute top-full left-0 w-full bg-white shadow-lg rounded-b-lg transition-all duration-300 ease-in-out z-50 max-h-96 overflow-y-auto">
                    <div className="p-4">
                        { paginatedResults.length > 0 ? (
                            paginatedResults.map((variant) => (
                                <div
                                    key={variant.id}
                                    onClick={() => handleSelect(variant)}
                                >
                                    {renderVariantItem(variant)}
                                </div>
                            ))
                        ) : (
                            <p>No results found</p>
                        )}
                    </div>
                    {totalPages > 1 && (
                        <div className="sticky bottom-0 bg-white p-4 border-t">
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                size="small"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ShopifySearch;
